import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StorageIcon from '@mui/icons-material/Storage';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';

import { LocalVideoService } from '../../services/LocalVideoService';
import {
  LocalVideoItem,
  StorageSummary,
  LocalVideoStatus,
} from '../../types/LocalVideo';

interface LocalVideoManagementViewProps {
  onDataLoaded?: (summary: StorageSummary | null) => void;
}

export const LocalVideoManagementView: React.FC<LocalVideoManagementViewProps> = ({
  onDataLoaded,
}) => {
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [videos, setVideos] = useState<LocalVideoItem[]>([]);
  const [summary, setSummary] = useState<StorageSummary | null>(null);
  const [statusFilter, setStatusFilter] = useState<LocalVideoStatus | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Dialog de confirmação para exclusão e lote
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    isDangerous?: boolean;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [videosRes, summaryRes] = await Promise.all([
        LocalVideoService.getVideos(statusFilter || undefined, page),
        LocalVideoService.getSummary(),
      ]);
      setVideos(videosRes.data);
      setTotalPages(videosRes.last_page);
      setSummary(summaryRes);
      if (onDataLoaded) {
        onDataLoaded(summaryRes);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Erro ao carregar vídeos locais.';
      setFeedback({
        type: 'error',
        message: msg,
      });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, onDataLoaded]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDispatchGcs = async (id: number) => {
    setActionLoadingId(id);
    setFeedback(null);
    try {
      await LocalVideoService.dispatchGcs(id);
      const msg = `Upload do vídeo #${id} enviado para a fila do GCS!`;
      setFeedback({ type: 'success', message: msg });
      toast.success(msg);
      loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Falha ao iniciar upload.';
      setFeedback({
        type: 'error',
        message: msg,
      });
      toast.error(msg);
    } finally {
      setActionLoadingId(null);
    }
  };

  const executeDispatchAll = async () => {
    setBatchLoading(true);
    setFeedback(null);
    try {
      const res = await LocalVideoService.dispatchAllPending();
      const msg = `${res.dispatched_count} vídeos foram enfileirados para envio ao GCS.`;
      setFeedback({
        type: 'success',
        message: msg,
      });
      toast.success(msg);
      loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Falha no processamento em lote.';
      setFeedback({
        type: 'error',
        message: msg,
      });
      toast.error(msg);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleDispatchAll = () => {
    setConfirmDialog({
      open: true,
      title: 'Enviar Todos os Vídeos Pendentes',
      message:
        'Deseja enfileirar o envio para o Google Cloud Storage (GCS) de todos os vídeos pendentes ou com falha?',
      isDangerous: false,
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        executeDispatchAll();
      },
    });
  };

  const executeDeleteLocal = async (id: number, isDone: boolean) => {
    setActionLoadingId(id);
    setFeedback(null);
    try {
      const res = await LocalVideoService.deleteLocalFile(id, !isDone);
      const msg = `Arquivo local #${id} excluído. Espaço liberado: ${res.bytes_freed_formatted || '0 B'}.`;
      setFeedback({
        type: 'success',
        message: msg,
      });
      toast.success(msg);
      loadData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Erro ao excluir arquivo local.';
      setFeedback({
        type: 'error',
        message: msg,
      });
      toast.error(msg);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteLocal = (id: number, isDone: boolean) => {
    if (isDone) {
      setConfirmDialog({
        open: true,
        title: 'Liberar Espaço em Disco',
        message:
          'O vídeo já foi enviado com sucesso e está seguro no Google Cloud Storage (GCS). Deseja excluir a cópia local para liberar espaço em disco?',
        isDangerous: false,
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          executeDeleteLocal(id, true);
        },
      });
    } else {
      setConfirmDialog({
        open: true,
        title: 'ATENÇÃO: Risco de Perda do Vídeo',
        message:
          'Este vídeo ainda NÃO foi enviado ao Google Cloud Storage (GCS). Excluir o arquivo local agora impedirá o envio para a nuvem e o arquivo será perdido permanentemente. Deseja realmente forçar a exclusão?',
        isDangerous: true,
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          executeDeleteLocal(id, false);
        },
      });
    }
  };

  const renderStatusChip = (status: LocalVideoStatus) => {
    switch (status) {
      case 'done':
        return <Chip size="small" icon={<CheckCircleOutlineIcon />} label="Concluído" color="success" />;
      case 'processing':
        return <Chip size="small" icon={<CircularProgress size={12} color="inherit" />} label="Enviando..." color="info" />;
      case 'failed':
        return <Chip size="small" icon={<ErrorOutlineIcon />} label="Falha" color="error" />;
      default:
        return <Chip size="small" label="Pendente" color="warning" />;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Alerta de Feedback Inline */}
      {feedback && (
        <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      {/* Resumo de Volumetria e Métricas */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography color="text.secondary" variant="caption" fontWeight={600}>
                  Espaço Local Ocupado
                </Typography>
                <Typography variant="h5" color="primary" sx={{ my: 0.5, fontWeight: 700 }}>
                  {summary.total_local_formatted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.local_files_count} arquivos em disco ({summary.total_attachments} anexos)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography color="text.secondary" variant="caption" fontWeight={600}>
                  Pendentes / Falhas
                </Typography>
                <Typography variant="h5" color="warning.main" sx={{ my: 0.5, fontWeight: 700 }}>
                  {summary.status_breakdown.pending + summary.status_breakdown.failed}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.status_breakdown.pending} pendentes · {summary.status_breakdown.failed} com falha
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography color="text.secondary" variant="caption" fontWeight={600}>
                  No GCS (Concluídos)
                </Typography>
                <Typography variant="h5" color="success.main" sx={{ my: 0.5, fontWeight: 700 }}>
                  {summary.status_breakdown.done}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload finalizado para a nuvem
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ py: 1.5, fontWeight: 600 }}
              startIcon={batchLoading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              onClick={handleDispatchAll}
              disabled={
                batchLoading ||
                (summary.status_breakdown.pending === 0 && summary.status_breakdown.failed === 0)
              }
            >
              {batchLoading ? 'Enviando em lote...' : 'Enviar Todos Pendentes'}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Barra de Filtros e Ações */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Atualizar dados">
            <span>
              <IconButton onClick={loadData} disabled={loading} size="small" color="primary">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Carregando registros...' : `${videos.length} vídeo(s) na página atual`}
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="filter-status-label">Filtrar por Status</InputLabel>
          <Select
            labelId="filter-status-label"
            value={statusFilter}
            label="Filtrar por Status"
            onChange={(e) => {
              setStatusFilter(e.target.value as LocalVideoStatus | '');
              setPage(1);
            }}
          >
            <MenuItem value="">Todos os status</MenuItem>
            <MenuItem value="pending">Pendentes</MenuItem>
            <MenuItem value="processing">Enviando</MenuItem>
            <MenuItem value="failed">Falhas</MenuItem>
            <MenuItem value="done">Concluídos</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabela de Arquivos */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: 'action.hover' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Arquivo Original</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contexto (Aluno / Estação / Evento)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tamanho Local</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Disco Local</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Buscando vídeos locais...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : videos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <StorageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    Nenhum vídeo local encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              videos.map((v) => {
                const isBusy = actionLoadingId === v.id;
                const isDone = v.status === 'done';

                return (
                  <TableRow key={v.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>#{v.id}</TableCell>
                    <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {v.original_name || 'Sem nome'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.created_at ? new Date(v.created_at).toLocaleString('pt-BR') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {v.context.evaluated_name || `Aluno ID ${v.object_id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.context.station_name || 'Estação'} · {v.context.event_name || 'Evento'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {v.local_file.exists ? (
                        <Typography variant="body2">{v.local_file.size_formatted}</Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Liberado
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderStatusChip(v.status)}</TableCell>
                    <TableCell>
                      {v.local_file.exists ? (
                        <Chip size="small" variant="outlined" color="primary" label="Em disco" />
                      ) : (
                        <Chip size="small" variant="outlined" label="Removido" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {/* Botão Enviar ao GCS */}
                        {v.local_file.exists && (
                          <Tooltip title="Disparar upload para o GCS">
                            <span>
                              <IconButton
                                aria-label={`Disparar upload para o GCS #${v.id}`}
                                size="small"
                                color="primary"
                                onClick={() => handleDispatchGcs(v.id)}
                                disabled={isBusy || v.status === 'processing'}
                              >
                                {isBusy ? <CircularProgress size={18} /> : <CloudUploadIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Link para o GCS se concluído */}
                        {isDone && v.gcs.url && (
                          <Tooltip title="Ver vídeo no GCS">
                            <IconButton
                              aria-label={`Ver vídeo no GCS #${v.id}`}
                              size="small"
                              href={v.gcs.url}
                              target="_blank"
                              color="info"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Botão Excluir Arquivo Local */}
                        {v.local_file.exists && (
                          <Tooltip
                            title={
                              isDone
                                ? 'Excluir arquivo local para liberar espaço em disco (já salvo no GCS)'
                                : 'Excluir arquivo local'
                            }
                          >
                            <span>
                              <IconButton
                                aria-label={`Excluir arquivo local #${v.id}`}
                                size="small"
                                color="error"
                                onClick={() => handleDeleteLocal(v.id, isDone)}
                                disabled={isBusy}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Diálogo de Confirmação Acessível */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {confirmDialog.isDangerous && <WarningAmberIcon color="error" />}
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            color={confirmDialog.isDangerous ? 'error' : 'primary'}
            variant="contained"
            autoFocus
          >
            {confirmDialog.isDangerous ? 'Excluir Definitivamente' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocalVideoManagementView;
